import React, { useEffect, useRef, useState, memo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAppContext } from '../../../providers/AppProvider';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  DataZoomComponent
} from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { Card } from 'react-bootstrap';
import { FallingLines } from 'react-loader-spinner';
import { faCropSimple } from '@fortawesome/free-solid-svg-icons';
import { isLiveTimeRange } from '../utils/chartUtils';
import { INFLUX_API } from '../../../config/api';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  LegendComponent,
  DataZoomComponent
]);

// Utility function to remove duplicates and sort data
const removeDuplicatesAndSort = data => {
  const sortedData = data.sort((a, b) => new Date(a[0]) - new Date(b[0]));

  return sortedData;
};

// Options for the chart
const getDefaultOptions = (theme, getThemeColor, data) => ({
  color: [
    getThemeColor('primary'),
    getThemeColor('info'),
    getThemeColor('success')
  ],
  title: {
    text: 'Machine Traverse Chart'
  },
  tooltip: {
    trigger: 'axis',
    padding: 10,
    backgroundColor: getThemeColor('gray-100'),
    borderColor: getThemeColor('gray-300'),
    textStyle: { color: getThemeColor('dark') },
    borderWidth: 1,
    transitionDuration: 0,
    axisPointer: {
      type: 'line',
      axis: 'x'
    },
    formatter: function (params) {
      const actPos = params[0].value[1];
      const inferiorLimit = params[1] ? params[1].value[1] : 'N/A';
      const superiorLimit = params[2] ? params[2].value[1] : 'N/A';
      const time = params[0].value[0];
      return `Time: ${dayjs(time).format(
        'HH:mm:ss'
      )}<br/>Actual Position: ${actPos.toFixed(2)}<br/>Inferior Limit: ${
        inferiorLimit !== 'N/A' ? inferiorLimit.toFixed(2) : inferiorLimit
      }<br/>Superior Limit: ${
        superiorLimit !== 'N/A' ? superiorLimit.toFixed(2) : superiorLimit
      }`;
    }
  },
  xAxis: {
    type: 'time',
    boundaryGap: false,
    axisLabel: {
      formatter: value => dayjs(value).format('HH:mm'),
      interval: 'auto',
      margin: 10
    },
    splitLine: {
      show: false
    }
  },
  yAxis: {
    position: 'right',
    axisPointer: { type: 'none' },
    axisTick: { show: false },
    splitLine: {
      show: false
    },
    axisLine: { show: false },
    axisLabel: { show: true }
  },
  series: [
    {
      name: 'Actual Position',
      type: 'line',
      data: data.TRAVER_ACT_POS,
      showSymbol: false,
      symbol: 'circle',
      smooth: true
    },
    {
      name: 'Inferior Limit',
      type: 'line',
      data: data.TRAVERSE_INFERIOR_LIMIT,
      lineStyle: {
        type: 'dashed',
        width: 1,
        color: getThemeColor('info')
      },
      showSymbol: false,
      symbol: 'circle'
    },
    {
      name: 'Superior Limit',
      type: 'line',
      data: data.TRAVERSE_SUPERIOR_LIMIT,
      lineStyle: {
        type: 'dashed',
        width: 1,
        color: getThemeColor('success')
      },
      showSymbol: false,
      symbol: 'circle'
    }
  ],
  grid: {
    right: 20,
    left: 20,
    bottom: '20px',
    top: '2%',
    containLabel: true
  },
  animation: true,
  dataZoom: [
    {
      type: 'inside',
      xAxisIndex: 0,
      start: 0,
      end: 100
    }
  ]
});

const MachineTraverseChart = ({
  bucket,
  machineName,
  fields,
  startTime,
  endTime
}) => {
  const chartRef = useRef(null);
  const [data, setData] = useState({});

  const [loading, setLoading] = useState(true); // Track loading state
  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  // Fetching and processing data with sorting and deduplication
  const fetchData = async () => {
    setLoading(true);
    try {
      const url = `${INFLUX_API}/api/influx/${machineName}/fields?fields=${fields}&start=${startTime}&end=${endTime}&bucket=${bucket}`;
      const response = await axios.get(url);
      const responseData = response.data;

      console.log('Fetched data:', responseData);

      // Construim state-ul strict din ce vine în response
      const newData = {};

      Object.keys(responseData).forEach(key => {
        // Sort + remove duplicates pe fiecare cheie
        newData[key] = removeDuplicatesAndSort(responseData[key] || []);
      });

      setData(newData);
      console.log('Final data:', newData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = isLiveTimeRange(endTime) ? setInterval(fetchData, 60000) : null;
    return () => clearInterval(intervalId);
  }, [machineName, fields, startTime, endTime]);

  if (loading) {
    return (
      <Card className="h-100">
        <Card.Body style={{ height: '316px', width: '100%' }}>
          <div style={styles.spinnerContainer}>
            <FallingLines
              color={theme === 'dark' ? 'white' : '#3874ff'}
              width="100"
              visible={true}
              ariaLabel="falling-circles-loading"
            />
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      <Card.Body>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={getDefaultOptions(theme, getThemeColor, data)}
          style={{ height: '316px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

const styles = {
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  }
};

export default memo(MachineTraverseChart);
